import { useQuery } from '@tanstack/react-query';
import { fetchMatchTeamOverview } from '@/lib/network';
import { parsePercentageToInt } from '@/utils/parsePercentageToInt';
import { MatchData } from '../types';

interface UseTeamOverviewProps {
  matchId: string | undefined;
  displayMatch: MatchData | null;
}

export function useTeamOverview({ matchId, displayMatch }: UseTeamOverviewProps) {
  const teamOverviewQuery = useQuery({
    queryKey: ['match', 'teamOverview', matchId],
    queryFn: () => {
      if (!matchId) throw new Error('matchId is required');
      return fetchMatchTeamOverview(matchId);
    },
    enabled: !!matchId,
  });

  const teamOverviewData = teamOverviewQuery.data?.data;

  const teamOverviewProps =
    teamOverviewData && displayMatch
      ? {
          hideSide: displayMatch.isAdditionalLeagueFixture
            ? !displayMatch.homeHasFantasyData
              ? 'left'
              : !displayMatch.awayHasFantasyData
                ? 'right'
                : undefined
            : undefined,
          leftTeam: {
            name: displayMatch.homeTeam.shortName,
            country: displayMatch.homeTeam.flag || displayMatch.homeTeam.code,
            form: teamOverviewData.home.form,
          },
          rightTeam: {
            name: displayMatch.awayTeam.shortName,
            country: displayMatch.awayTeam.flag || displayMatch.awayTeam.code,
            form: teamOverviewData.away.form,
          },
          rows: [
            {
              label: 'Top Picked Player',
              leftPlayerName: teamOverviewData.home.topPickedPlayer.name,
              leftPlayerCountry:
                teamOverviewData.home.topPickedPlayer.countryFlagKey || displayMatch.homeTeam.code,
              leftValue:
                String(parsePercentageToInt(teamOverviewData.home.topPickedPlayer.ownershipPct)) +
                  '%' || '-',
              rightValue:
                String(parsePercentageToInt(teamOverviewData.away.topPickedPlayer.ownershipPct)) +
                  '%' || '-',
              rightPlayerName: teamOverviewData.away.topPickedPlayer.name,
              rightPlayerCountry:
                teamOverviewData.away.topPickedPlayer.countryFlagKey || displayMatch.awayTeam.code,
            },
            {
              label: 'Highest Scoring Player',
              leftPlayerName: teamOverviewData.home.highestScoringPlayer.name,
              leftPlayerCountry:
                teamOverviewData.home.highestScoringPlayer.countryFlagKey ||
                displayMatch.homeTeam.code,
              leftValue: String(teamOverviewData.home.highestScoringPlayer.totalPoints ?? '-'),
              rightValue: String(teamOverviewData.away.highestScoringPlayer.totalPoints ?? '-'),
              rightPlayerName: teamOverviewData.away.highestScoringPlayer.name,
              rightPlayerCountry:
                teamOverviewData.away.highestScoringPlayer.countryFlagKey ||
                displayMatch.awayTeam.code,
            },
            {
              label: 'Most In-Form Player',
              leftPlayerName: teamOverviewData.home.mostInFormPlayer.name,
              leftPlayerCountry:
                teamOverviewData.home.mostInFormPlayer.countryFlagKey || displayMatch.homeTeam.code,
              leftValue: String(teamOverviewData.home.mostInFormPlayer.points) || '-',
              rightValue: String(teamOverviewData.away.mostInFormPlayer.points) || '-',
              rightPlayerName: teamOverviewData.away.mostInFormPlayer.name,
              rightPlayerCountry:
                teamOverviewData.away.mostInFormPlayer.countryFlagKey || displayMatch.awayTeam.code,
            },
          ],
        }
      : null;

  return { teamOverviewQuery, teamOverviewProps };
}
